import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { Text } from "../Text";
import { useHoverState } from "../../hooks/useHoverState";
import { ToggleContext } from "./ToggleContext";
import { motion } from 'framer-motion';
import { mergePropsAndRefs } from "../../utils";
import VisuallyHidden from '@reach/visually-hidden';
export var Toggle = React.forwardRef(function (_ref, forwardedRef) {
  var _ref$block = _ref.block,
      block = _ref$block === void 0 ? false : _ref$block,
      checked = _ref.checked,
      onChange = _ref.onChange,
      className = _ref.className,
      disabled = _ref.disabled,
      children = _ref.children,
      name = _ref.name,
      onFocusProp = _ref.onFocus,
      onBlurProp = _ref.onBlur,
      other = _objectWithoutProperties(_ref, ["block", "checked", "onChange", "className", "disabled", "children", "name", "onFocus", "onBlur"]);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      tapped = _useState2[0],
      setTapped = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      focused = _useState4[0],
      setFocused = _useState4[1];

  var _useHoverState = useHoverState(),
      isHovering = _useHoverState.isHovering,
      bind = _useHoverState.bind;

  var onTapStart = function onTapStart() {
    return setTapped(true);
  };

  var onTapEnd = function onTapEnd() {
    return setTapped(false);
  };

  var onFocus = function onFocus(e) {
    if (onFocusProp) {
      onFocusProp(e);
    }

    if (e.target.hasAttribute('data-focus-visible-added')) {
      setFocused(true);
    }
  };

  var onBlur = function onBlur(e) {
    if (onBlurProp) {
      onBlurProp(e);
    }

    setFocused(false);
  };

  var toggleContext = useMemo(function () {
    return {
      tapped: tapped,
      focused: focused,
      checked: checked,
      disabled: disabled,
      hovering: isHovering
    };
  }, [tapped, focused, checked, disabled, isHovering]);
  return /*#__PURE__*/React.createElement(StyledLabel, _extends({
    onTapStart: onTapStart,
    onTapCancel: onTapEnd,
    onTap: onTapEnd,
    className: className,
    $block: block,
    $disabled: disabled
  }, mergePropsAndRefs(bind, {
    ref: forwardedRef
  }, other)), /*#__PURE__*/React.createElement(VisuallyHidden, null, /*#__PURE__*/React.createElement("input", {
    type: "checkbox",
    checked: checked,
    onChange: onChange,
    disabled: disabled,
    onFocus: onFocus,
    onBlur: onBlur,
    name: name
  })), /*#__PURE__*/React.createElement(ToggleContext.Provider, {
    value: toggleContext
  }, children));
});
var StyledLabel = styled(motion.label).withConfig({
  displayName: "Toggle__StyledLabel",
  componentId: "vapor__y3t28i-0"
})(["display:", ";align-items:center;width:", ";cursor:", ";"], function (p) {
  return p.$block ? 'flex' : 'inline-flex';
}, function (p) {
  return p.$block ? '100%' : 'auto';
}, function (p) {
  return p.$disabled ? 'not-allowed' : 'pointer';
});
var StyledText = styled(Text).withConfig({
  displayName: "Toggle__StyledText",
  componentId: "vapor__y3t28i-1"
})(["user-select:none;"]);
export var ToggleLabel = React.forwardRef(function (_ref2, forwardedRef) {
  var visuallyHiddenProp = _ref2.visuallyHidden,
      other = _objectWithoutProperties(_ref2, ["visuallyHidden"]);

  var label = /*#__PURE__*/React.createElement(StyledText, _extends({
    ref: forwardedRef,
    color: "primary"
  }, other));
  return visuallyHiddenProp ? /*#__PURE__*/React.createElement(VisuallyHidden, null, label) : label;
});